import { EnvelopeSimpleIcon, PhoneIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';

import { Modal } from '@/components/feedback/modal';
import { formatPhoneUS } from '@/lib/utils/format';

import { PROFILE_COPY } from '../constants';
import { useProfile } from '../hooks';

import styles from './contact-us-modal.module.scss';

export type ContactUsModalProps = { open: boolean; onClose: () => void };

// Figma 1422:611335: a 38.75px glyph, rounded to the nearest whole pixel.
const ICON_SIZE = 39;

/**
 * Contact Us (US-117535, Figma 1422:611326). `<TopNav>` mounts this on every page, so it must not
 * read the profile itself — the shared `<Modal>` destroys only its children while hidden, which is
 * why `useProfile()` lives in `<ContactUsTiles>` and runs only once the modal is open.
 */
export function ContactUsModal({ open, onClose }: ContactUsModalProps) {
  return (
    <Modal open={open} onClose={onClose} title={PROFILE_COPY.CONTACT_US} size="sm" hideFooter>
      <ContactUsTiles />
    </Modal>
  );
}

// "-" is read aloud as "dash"; the accessible name says what it means instead (as in the panel).
const unavailable = (
  <>
    <span aria-hidden>{PROFILE_COPY.UNAVAILABLE}</span>
    <span className={styles.srOnly}>{PROFILE_COPY.UNAVAILABLE_SR}</span>
  </>
);

/**
 * Reads `support` from the panel's `useProfile()` cache. Unavailable (or not loaded yet) renders
 * "-" as plain text, never a dead link (grilling Q10).
 */
function ContactUsTiles() {
  const support = useProfile().data?.support ?? null;
  return (
    <div className={styles.tiles}>
      <Tile
        icon={<PhoneIcon size={ICON_SIZE} />}
        href={support ? `tel:+1${support.phone}` : undefined}
      >
        {support ? `+1 ${formatPhoneUS(support.phone)}` : unavailable}
      </Tile>
      <Tile
        icon={<EnvelopeSimpleIcon size={ICON_SIZE} />}
        href={support ? `mailto:${support.email}` : undefined}
      >
        {support ? support.email : unavailable}
      </Tile>
    </div>
  );
}

function Tile({ icon, href, children }: { icon: ReactNode; href?: string; children: ReactNode }) {
  const body = (
    <>
      <span className={styles.icon} aria-hidden>
        {icon}
      </span>
      <span className={styles.label}>{children}</span>
    </>
  );
  return href ? (
    <a className={[styles.tile, styles.link].join(' ')} href={href}>
      {body}
    </a>
  ) : (
    <div className={styles.tile}>{body}</div>
  );
}
